import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import dayjs, { Dayjs } from "dayjs";
import { ExternalLink, Paperclip, Users } from "lucide-react";
import { DatePicker, Image, Select } from "antd";
import { getAccessToken, getEmployeeId } from "@/lib/storeManager";
import { get } from "@/services/axiosUitils";
import {
  CURRENT_USER_DETAILS_NAVBAR_KEY,
  PtsCurrentUserDetailsService,
  PtsEmployeeService,
  UploadService,
  employeeOptionFromCurrentUserDetails,
} from "@/services/APIs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { isEmployee } from "../../utils/rolePermissions";

type ApiDateHours = {
  date: string;
  allocated_hours: number;
  remaining_hours?: number;
  available_hours?: number;
};

type ApiWeekBlock = {
  week_label: string;
  week_start: string;
  week_end: string;
  dates: ApiDateHours[];
};

type ApiEmployeeBlock = {
  emp_id: string;
  emp_name: string;
  week_wise: ApiWeekBlock[];
};

type ApiTask = {
  task: string;
  task_name: string;
  description?: string;
  descriptions?: string;
  task_description?: string;
  attachment?: unknown;
  attachments?: unknown;
  task_attachment?: unknown;
  emp_list: ApiEmployeeBlock[];
  task_total_allocated_hours?: number;
};

type ApiProject = {
  project: string;
  project_name: string;
  tasks_list: ApiTask[];
  project_total_allocated_hours?: number;
};

type ApiSprint = {
  sprint_id: string;
  sprint_name: string;
  project_list: ApiProject[];
  sprint_total_allocated_hours?: number;
  sprint_total_week_wise?: ApiWeekBlock[];
};

type EmployeeAllocationRangeResponse = {
  employee_id: string;
  start_date: string;
  end_date: string;
  sprints: ApiSprint[];
  daily_capacity?: Array<{
    date: string;
    allocated_hours: number;
    available_hours: number;
  }>;
  daily_capacity_week_wise: Array<{
    week_label: string;
    week_start: string;
    week_end: string;
    dates: Array<{
      date: string;
      allocated_hours: number;
      available_hours: number;
    }>;
  }>;
};

type TaskRow = {
  taskName: string;
  description?: string;
  attachmentId?: string;
  attachmentUrl?: string;
  attachmentName?: string;
  dailyHours: Record<string, number>;
  totalAllocatedHours: number;
};

const formatApiDate = (d: Dayjs) => d.format("DD-MM-YYYY");
const formatShortDay = (isoDate: string) => dayjs(isoDate).format("ddd DD");

const isLikelyImageFile = (nameOrUrl: string) =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(nameOrUrl);

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const pickAttachmentMeta = (task: ApiTask) => {
  const raw =
    task.attachment ?? task.attachments ?? task.task_attachment ?? null;
  if (!raw) {
    return {
      id: undefined as string | undefined,
      url: undefined as string | undefined,
      name: undefined as string | undefined,
    };
  }
  if (typeof raw === "string") {
    const asString = String(raw).trim();
    if (UUID_RE.test(asString)) {
      return {
        id: asString,
        url: undefined as string | undefined,
        name: undefined as string | undefined,
      };
    }
    return {
      id: undefined as string | undefined,
      url: asString,
      name: asString.split("/").pop() ?? "Attachment",
    };
  }
  if (typeof raw === "object") {
    const obj = raw as Record<string, any>;
    const idRaw = obj.id ?? obj.file_id ?? obj.uuid ?? obj.pk;
    const id =
      idRaw !== null && UUID_RE.test(String(idRaw)) ? String(idRaw) : undefined;
    const url =
      obj.url ??
      obj.file_url ??
      obj.download_url ??
      obj.path ??
      obj.file ??
      obj.src;
    const name =
      obj.name ??
      obj.file_name ??
      obj.filename ??
      (typeof url === "string" ? url.split("/").pop() : undefined);
    if (id || url) {
      return {
        id,
        url: url ? String(url) : undefined,
        name: name ? String(name) : "Attachment",
      };
    }
  }
  return {
    id: undefined as string | undefined,
    url: undefined as string | undefined,
    name: undefined as string | undefined,
  };
};

const groupDaysByWeek = (
  days: string[],
  weekWise: EmployeeAllocationRangeResponse["daily_capacity_week_wise"]
) => {
  if (weekWise?.length) {
    return weekWise.map(week => ({
      weekLabel: week.week_label,
      days: week.dates.map(d => d.date).filter(d => days.includes(d)),
    }));
  }

  const byWeek = new Map<string, string[]>();
  days.forEach(day => {
    const key = `${dayjs(day).startOf("week").format("YYYY-MM-DD")}-${dayjs(day)
      .endOf("week")
      .format("YYYY-MM-DD")}`;
    const label = `Week ${dayjs(day).startOf("week").format("DD MMM")}`;
    if (!byWeek.has(`${key}|${label}`)) {
      byWeek.set(`${key}|${label}`, []);
    }
    byWeek.get(`${key}|${label}`)?.push(day);
  });
  return Array.from(byWeek.entries()).map(([key, weekDays]) => ({
    weekLabel: key.split("|")[1],
    days: weekDays,
  }));
};

const SprintTaskAllocationPage = () => {
  const currentEmployeeId = getEmployeeId();
  const userIsEmployee = isEmployee();

  const [employeeId, setEmployeeId] = useState<string | undefined>(() => {
    if (userIsEmployee && currentEmployeeId) {
      return currentEmployeeId;
    }
    return undefined;
  });
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (userIsEmployee && currentEmployeeId) {
      setEmployeeId(currentEmployeeId);
    }
  }, [currentEmployeeId, userIsEmployee]);

  // PTS Employee: GET /pts_app/current-user-details/ (not PTS Emp dynamic-model)
  const { data: currentUserDetails } = useQuery({
    queryKey: [...CURRENT_USER_DETAILS_NAVBAR_KEY],
    queryFn: () => PtsCurrentUserDetailsService.getCurrentUserDetails(),
    enabled: userIsEmployee && !!getAccessToken(),
  });

  // Other roles: full employee list for dropdown
  const { data: employeeRaw } = useQuery({
    queryKey: ["sprint-task-allocation-employee-options", "all"],
    queryFn: () => PtsEmployeeService.getEmployees({}, 1, 500),
    enabled: !userIsEmployee,
    // Old API for employee role (commented):
    // PtsEmployeeService.getEmployees({ id__eq: currentEmployeeId }, 1, 500)
  });

  const employeeOptions = useMemo(() => {
    if (userIsEmployee) {
      const option = employeeOptionFromCurrentUserDetails(currentUserDetails);
      if (option) {
        return [option];
      }
      if (currentEmployeeId) {
        return [{ value: currentEmployeeId, label: "Employee" }];
      }
      return [];
    }

    const rawList = Array.isArray(employeeRaw)
      ? employeeRaw
      : (employeeRaw?.results ?? employeeRaw?.data ?? []);
    if (!Array.isArray(rawList)) {
      return [];
    }

    return rawList.map((emp: any) => {
      const first = emp?.first_name ?? emp?.firstname ?? "";
      const last = emp?.last_name ?? emp?.lastname ?? "";
      const fullName = `${first} ${last}`.trim();
      return {
        value: String(emp?.id ?? emp?.ID ?? ""),
        label:
          fullName ||
          String(emp?.name ?? emp?.emp_name ?? emp?.id ?? "Employee"),
      };
    });
  }, [currentEmployeeId, currentUserDetails, employeeRaw, userIsEmployee]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "employee-allocation-range",
      employeeId,
      startDate ? formatApiDate(startDate) : "",
      endDate ? formatApiDate(endDate) : "",
    ],
    queryFn: () =>
      get<EmployeeAllocationRangeResponse>(
        "/pts_app/employee-allocation-range/",
        {
          params: {
            employee_id: employeeId,
            start_date: startDate ? formatApiDate(startDate) : undefined,
            end_date: endDate ? formatApiDate(endDate) : undefined,
          },
        }
      ),
    enabled: !!employeeId && !!startDate && !!endDate,
  });
  const canLoadData = !!employeeId && !!startDate && !!endDate;

  const allDays = useMemo(() => {
    const dateSet = new Set<string>();
    if (data?.daily_capacity_week_wise?.length) {
      data.daily_capacity_week_wise.forEach(week => {
        (week.dates ?? []).forEach(d => {
          if (d.date) {
            dateSet.add(d.date);
          }
        });
      });
      return Array.from(dateSet).sort(
        (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()
      );
    }
    if (data?.daily_capacity?.length) {
      data.daily_capacity.forEach(d => {
        if (d.date) {
          dateSet.add(d.date);
        }
      });
      return Array.from(dateSet).sort(
        (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()
      );
    }
    return [];
  }, [data?.daily_capacity_week_wise, data?.daily_capacity]);

  const weeks = useMemo(
    () => groupDaysByWeek(allDays, data?.daily_capacity_week_wise ?? []),
    [allDays, data?.daily_capacity_week_wise]
  );

  const filteredSprints = useMemo(
    () =>
      (data?.sprints ?? [])
        .map(sprint => {
          const rows: TaskRow[] = [];
          sprint.project_list.forEach(project => {
            project.tasks_list.forEach(task => {
              const emp = task.emp_list?.find(
                e => e.emp_id === data?.employee_id
              );
              const attachmentMeta = pickAttachmentMeta(task);
              const dailyHours: Record<string, number> = {};
              emp?.week_wise?.forEach(week => {
                week.dates.forEach(d => {
                  dailyHours[d.date] = Number(d.allocated_hours ?? 0);
                });
              });
              rows.push({
                taskName: task.task_name,
                description:
                  task.description ??
                  task.descriptions ??
                  task.task_description,
                attachmentId: attachmentMeta.id,
                attachmentUrl: attachmentMeta.url,
                attachmentName: attachmentMeta.name,
                dailyHours,
                totalAllocatedHours: Number(
                  task.task_total_allocated_hours ?? 0
                ),
              });
            });
          });
          const sprintDailyFromTotal: Record<string, number> = {};
          (sprint.sprint_total_week_wise ?? []).forEach(week => {
            (week.dates ?? []).forEach(day => {
              sprintDailyFromTotal[day.date] = Number(day.allocated_hours ?? 0);
            });
          });
          const projectNames = sprint.project_list
            .map(p => p.project_name)
            .filter(Boolean);
          return {
            sprintId: sprint.sprint_id,
            sprintName: sprint.sprint_name,
            projectNameText:
              projectNames.length <= 1
                ? (projectNames[0] ?? "-")
                : `${projectNames[0]} +${projectNames.length - 1}`,
            rows,
            sprintDailyFromTotal,
            sprintTotalAllocatedHours: Number(
              sprint.sprint_total_allocated_hours ?? 0
            ),
          };
        })
        .filter(s => s.rows.length > 0),
    [data?.sprints, data?.employee_id]
  );

  const attachmentIds = useMemo(
    () =>
      Array.from(
        new Set(
          filteredSprints
            .flatMap(s => s.rows)
            .map(r => r.attachmentId)
            .filter((id): id is string => Boolean(id))
        )
      ),
    [filteredSprints]
  );

  const { data: attachmentById = {} } = useQuery({
    queryKey: ["sprint-task-allocation-file-meta", attachmentIds],
    queryFn: async () => {
      const entries = await Promise.all(
        attachmentIds.map(async id => {
          try {
            const file = await UploadService.getFileById({ id });
            const fullPath = file?.file_path as string | undefined;
            const thumbPath = file?.file_path_thumbnail as string | undefined;
            return [
              id,
              {
                name: String(
                  file?.file_original_name ?? file?.file_name ?? "Attachment"
                ),
                previewUrl: thumbPath ?? fullPath ?? null,
                downloadUrl: fullPath ?? thumbPath ?? null,
              },
            ] as const;
          } catch {
            return [id, null] as const;
          }
        })
      );
      return Object.fromEntries(entries) as Record<
        string,
        {
          name: string;
          previewUrl: string | null;
          downloadUrl: string | null;
        } | null
      >;
    },
    enabled: attachmentIds.length > 0,
  });

  const capacityAllocatedByDate = useMemo(() => {
    const map: Record<string, number> = {};
    if (data?.daily_capacity?.length) {
      data.daily_capacity.forEach(d => {
        map[d.date] = Number(d.allocated_hours ?? 0);
      });
      return map;
    }
    (data?.daily_capacity_week_wise ?? []).forEach(week => {
      (week.dates ?? []).forEach(d => {
        map[d.date] = Number(d.allocated_hours ?? 0);
      });
    });
    return map;
  }, [data?.daily_capacity, data?.daily_capacity_week_wise]);

  const capacityAvailableByDate = useMemo(() => {
    const map: Record<string, number> = {};
    if (data?.daily_capacity?.length) {
      data.daily_capacity.forEach(d => {
        map[d.date] = Number(d.available_hours ?? 0);
      });
      return map;
    }
    (data?.daily_capacity_week_wise ?? []).forEach(week => {
      (week.dates ?? []).forEach(d => {
        map[d.date] = Number(d.available_hours ?? 0);
      });
    });
    return map;
  }, [data?.daily_capacity, data?.daily_capacity_week_wise]);

  return (
    <div className="space-y-6 p-4 sm:p-0">
      <div>
        <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
          Sprint Task Allocation
        </h1>
        <p className="text-sm text-[#627384]">
          Sprint-wise task allocation view with week/day capacity tracking
        </p>
      </div>

      <Card className="border border-gray-200 bg-white">
        {/* <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Filter className="h-4 w-4 text-primary" />
            Filters
          </CardTitle>
        </CardHeader> */}
        <CardContent className="pt-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <span className="text-sm font-medium text-gray-700">
                Employee
              </span>
              <Select
                allowClear={!userIsEmployee}
                showSearch
                optionFilterProp="label"
                className="w-full"
                value={employeeId}
                onChange={v => setEmployeeId(v)}
                disabled={userIsEmployee}
                placeholder="Select employee"
                options={employeeOptions}
              />
            </div>
            <div className="space-y-1">
              <span className="text-sm font-medium text-gray-700">
                Start Date
              </span>
              <DatePicker
                className="w-full"
                format="DD-MM-YYYY"
                value={startDate}
                onChange={val => setStartDate(val)}
                placeholder="Select start date"
              />
            </div>
            <div className="space-y-1">
              <span className="text-sm font-medium text-gray-700">
                End Date
              </span>
              <DatePicker
                className="w-full"
                format="DD-MM-YYYY"
                value={endDate}
                onChange={val => setEndDate(val)}
                placeholder="Select end date"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {!canLoadData ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-gray-700">
            Select employee, start date, and end date to load allocation data.
          </CardContent>
        </Card>
      ) : null}

      {canLoadData && (isLoading || isFetching) && (
        <div className="flex justify-center py-10">
          <LoadingSpinner />
        </div>
      )}

      {canLoadData &&
        !isLoading &&
        !isFetching &&
        filteredSprints.length === 0 && (
          <Card>
            <CardContent className="text-muted-foreground py-8 text-center text-sm">
              No allocation data found for selected filters.
            </CardContent>
          </Card>
        )}

      {canLoadData &&
        !isLoading &&
        !isFetching &&
        filteredSprints.map(sprint => {
          const hasMultipleWeeks = weeks.length > 1;
          return (
            <Card key={sprint.sprintId}>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  {/* <Target className="h-4 w-4 text-primary" /> */}
                  <div className="h-[0.6rem] w-[0.6rem] rounded-full bg-green-600" />
                  <CardTitle className="text-sm font-semibold">
                    {sprint.sprintName}
                  </CardTitle>
                  <span className="text-muted-foreground text-xs">
                    - {sprint.projectNameText}
                  </span>
                  <Badge variant="outline" className="text-[10px]">
                    {sprint.rows.length} tasks
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      {hasMultipleWeeks && (
                        <TableRow className="border-0 hover:bg-transparent">
                          <TableHead className="bg-background sticky left-0 z-10 min-w-[220px]" />
                          {weeks.map(week => (
                            <TableHead
                              key={`${week.weekLabel}`}
                              colSpan={week.days.length}
                              className="text-primary border-border/40 h-auto border-l border-r py-1 text-center text-[10px] font-bold"
                            >
                              {week.weekLabel}
                            </TableHead>
                          ))}
                          <TableHead className="min-w-[70px]" />
                        </TableRow>
                      )}
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="bg-muted sticky left-0 z-10 h-auto min-w-[220px] py-1.5 text-[10px] text-gray-500 md:w-[26vw] md:min-w-[220px] md:max-w-xl">
                          Task
                        </TableHead>
                        {allDays.map((day, i) => {
                          const isWeekStart =
                            hasMultipleWeeks &&
                            i > 0 &&
                            weeks.some(w => w.days[0] === day);
                          return (
                            <TableHead
                              key={day}
                              className={`h-auto min-w-[56px] whitespace-nowrap py-1.5 text-center text-[10px] ${isWeekStart ? "border-border/40 border-l" : ""}`}
                            >
                              {formatShortDay(day)}
                            </TableHead>
                          );
                        })}
                        <TableHead className="h-auto min-w-[70px] py-1.5 text-center text-[10px] font-semibold">
                          Total
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sprint.rows.map(row => {
                        const taskTotal = Number(row.totalAllocatedHours ?? 0);
                        const resolvedAttachment = row.attachmentId
                          ? attachmentById[row.attachmentId]
                          : null;
                        const attachmentName =
                          resolvedAttachment?.name ??
                          row.attachmentName ??
                          "Attachment";
                        const attachmentUrl =
                          resolvedAttachment?.downloadUrl ??
                          row.attachmentUrl ??
                          null;
                        const attachmentPreviewUrl =
                          resolvedAttachment?.previewUrl ??
                          row.attachmentUrl ??
                          null;
                        const canShowImagePreview = Boolean(
                          attachmentPreviewUrl &&
                            isLikelyImageFile(
                              attachmentName || attachmentPreviewUrl
                            )
                        );
                        return (
                          <TableRow key={`${row.taskName}`}>
                            <TableCell className="bg-muted sticky left-0 z-10 py-1.5 text-xs font-medium md:w-[26vw] md:min-w-[220px] md:max-w-xl">
                              <div className="min-w-0 max-w-full space-y-1">
                                <div className="break-words text-xs font-semibold text-[#111827]">
                                  {row.taskName}
                                </div>
                                {row.description ? (
                                  <p className="break-words text-[11px] font-normal leading-relaxed text-[#6b7280]">
                                    {row.description}
                                  </p>
                                ) : null}
                                {attachmentUrl ? (
                                  <div className="task-attachment-preview mt-1 flex min-h-[3.2rem] w-full min-w-0 max-w-full items-center gap-2 rounded-lg border border-gray-200 bg-white px-2 py-1.5">
                                    {canShowImagePreview ? (
                                      <div className="relative h-10 w-16 overflow-hidden rounded border border-gray-200 bg-white">
                                        <Image
                                          src={
                                            attachmentPreviewUrl ?? undefined
                                          }
                                          alt={attachmentName}
                                          className="h-full w-full object-cover"
                                          preview={{
                                            src: attachmentUrl,
                                            mask: (
                                              <span className="text-xs font-medium">
                                                Preview
                                              </span>
                                            ),
                                          }}
                                        />
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          window.open(
                                            attachmentUrl,
                                            "_blank",
                                            "noopener,noreferrer"
                                          )
                                        }
                                        className="bg-muted hover:bg-muted/80 flex h-10 w-16 items-center justify-center rounded border border-dashed border-gray-300"
                                      >
                                        <Paperclip className="text-muted-foreground h-4 w-4" />
                                      </button>
                                    )}
                                    <a
                                      href={attachmentUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex min-w-0 flex-1 items-center gap-1 text-[11px] text-blue-600 hover:text-blue-500 hover:underline"
                                      title={attachmentName}
                                    >
                                      <Paperclip className="h-3 w-3 shrink-0" />
                                      <span className="min-w-0 flex-1 truncate">
                                        {attachmentName}
                                      </span>
                                      <ExternalLink className="h-3 w-3 shrink-0" />
                                    </a>
                                  </div>
                                ) : null}
                              </div>
                            </TableCell>
                            {allDays.map((day, i) => {
                              const isWeekStart =
                                hasMultipleWeeks &&
                                i > 0 &&
                                weeks.some(w => w.days[0] === day);
                              const hrs = Number(row.dailyHours[day] ?? 0);
                              return (
                                <TableCell
                                  key={day}
                                  className={`py-1.5 text-center text-xs ${isWeekStart ? "border-border/40 border-l" : ""}`}
                                >
                                  {hrs > 0 ? (
                                    <span className="font-semibold">
                                      {hrs}h
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground/40">
                                      -
                                    </span>
                                  )}
                                </TableCell>
                              );
                            })}
                            <TableCell className="py-1.5 text-center">
                              <Badge
                                variant="outline"
                                className="font-mono text-[10px]"
                              >
                                {taskTotal}h
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow className="bg-muted/30 hover:bg-muted/40 font-semibold">
                        <TableCell className="bg-muted sticky left-0 z-10 py-1.5 text-xs md:w-[26vw] md:min-w-[220px] md:max-w-xl">
                          Sprint Total
                        </TableCell>
                        {allDays.map((day, i) => {
                          const isWeekStart =
                            hasMultipleWeeks &&
                            i > 0 &&
                            weeks.some(w => w.days[0] === day);
                          const dayTotal = Number(
                            sprint.sprintDailyFromTotal?.[day] ?? 0
                          );
                          return (
                            <TableCell
                              key={day}
                              className={`bg-muted py-1.5 text-center text-xs ${isWeekStart ? "border-border/40 border-l" : ""}`}
                            >
                              {dayTotal > 0 ? `${dayTotal}h` : "-"}
                            </TableCell>
                          );
                        })}
                        <TableCell className="bg-muted py-1.5 text-center text-xs">
                          {Number(sprint.sprintTotalAllocatedHours ?? 0)}h
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          );
        })}

      {canLoadData && !isLoading && !isFetching && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Users className="text-primary h-3.5 w-3.5" />
              Daily Capacity Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  {weeks.length > 1 && (
                    <TableRow className="border-0 hover:bg-transparent">
                      <TableHead className="bg-background sticky left-0 z-10 min-w-[110px]" />
                      {weeks.map(week => (
                        <TableHead
                          key={`${week.weekLabel}`}
                          colSpan={week.days.length}
                          className="text-primary border-border/40 h-auto border-l border-r py-1 text-center text-[10px] font-bold"
                        >
                          {week.weekLabel}
                        </TableHead>
                      ))}
                    </TableRow>
                  )}
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="bg-muted sticky left-0 z-10 h-auto min-w-[110px] py-1.5 text-[10px]" />
                    {allDays.map((day, i) => {
                      const isWeekStart =
                        weeks.length > 1 &&
                        i > 0 &&
                        weeks.some(w => w.days[0] === day);
                      return (
                        <TableHead
                          key={day}
                          className={`h-auto min-w-[56px] whitespace-nowrap py-1.5 text-center text-[10px] ${isWeekStart ? "border-border/40 border-l" : ""}`}
                        >
                          {formatShortDay(day)}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="bg-muted sticky left-0 z-10 py-1.5 text-xs font-medium">
                      Allocated
                    </TableCell>
                    {allDays.map((day, i) => {
                      const total = Number(capacityAllocatedByDate[day] ?? 0);
                      const isOver =
                        Number(capacityAvailableByDate[day] ?? 0) < 0;
                      const isWeekStart =
                        weeks.length > 1 &&
                        i > 0 &&
                        weeks.some(w => w.days[0] === day);
                      return (
                        <TableCell
                          key={day}
                          className={`py-1.5 text-center text-xs font-semibold ${isWeekStart ? "border-border/40 border-l" : ""} ${isOver ? "text-destructive bg-destructive/5" : total > 0 ? "text-foreground" : "text-muted-foreground/40"}`}
                        >
                          {total > 0 ? `${total}h` : "-"}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell className="bg-muted sticky left-0 z-10 py-1.5 text-xs font-medium">
                      Available
                    </TableCell>
                    {allDays.map((day, i) => {
                      const available = Number(
                        capacityAvailableByDate[day] ?? 0
                      );
                      const isWeekStart =
                        weeks.length > 1 &&
                        i > 0 &&
                        weeks.some(w => w.days[0] === day);
                      return (
                        <TableCell
                          key={day}
                          className={`py-1.5 text-center text-xs ${isWeekStart ? "border-border/40 border-l" : ""} ${available < 0 ? "text-destructive font-semibold" : available > 0 ? "font-semibold text-[hsl(var(--success))]" : "text-muted-foreground/40"}`}
                        >
                          {`${available}h`}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SprintTaskAllocationPage;
